import { Tick } from 'chart.js';

/**
 * Generates appropriate tick values for binary units (KiB, MiB, GiB, TiB)
 * @param min Minimum value on the axis
 * @param max Maximum value on the axis
 * @param scaleFactor Scale factor from getBestUnitForRange
 * @param maxTicksLimit Maximum number of ticks to generate
 * @returns Array of tick objects with values
 */
export function generateBinaryUnitTicks(
    min: number, 
    max: number, 
    scaleFactor: number,
    maxTicksLimit: number = 5
): { value: number }[] {
    const scaledMin = min / scaleFactor;
    const scaledMax = max / scaleFactor;
    const scaledRange = scaledMax - scaledMin;
    
    // Find a nice step size (powers of 2 or multiples of nice numbers)
    let step: number;
    if (scaledRange <= 10) {
        step = 1;
    } else if (scaledRange <= 20) {
        step = 2;
    } else if (scaledRange <= 50) {
        step = 5;
    } else if (scaledRange <= 100) {
        step = 10;
    } else if (scaledRange <= 256) {
        step = 32;
    } else if (scaledRange <= 512) {
        step = 64;
    } else if (scaledRange <= 1024) {
        step = 128;
    } else {
        step = Math.pow(2, Math.ceil(Math.log2(scaledRange / 8)));
    }
    
    // Generate new ticks
    const newTicks: { value: number }[] = [];
    const scaledStep = step * scaleFactor;
    const start = Math.ceil(min / scaledStep) * scaledStep;
    
    for (let value = start; value <= max; value += scaledStep) {
        newTicks.push({ value });
    }
    
    // Ensure we have min and max
    if (newTicks.length > 0 && newTicks[0].value > min) {
        newTicks.unshift({ value: min });
    }
    if (newTicks.length > 0 && newTicks[newTicks.length - 1].value < max) {
        newTicks.push({ value: max });
    }
    
    // Limit to reasonable number of ticks
    if (newTicks.length > maxTicksLimit) {
        // Keep first, last, and evenly spaced ticks
        const filtered: { value: number }[] = [];
        const spacing = Math.floor(newTicks.length / (maxTicksLimit - 1));
        
        for (let i = 0; i < newTicks.length; i += spacing) {
            filtered.push(newTicks[i]);
        }
        
        // Always include the last tick
        if (filtered[filtered.length - 1].value !== newTicks[newTicks.length - 1].value) {
            filtered.push(newTicks[newTicks.length - 1]);
        }
        
        return filtered;
    }
    
    return newTicks;
}

/**
 * Checks if a unit is a binary unit (KiB, MiB, GiB, TiB)
 * @param displayName The display name of the unit
 * @returns true if it's a binary unit
 */
export function isBinaryUnit(displayName: string | undefined): boolean {
    return displayName !== undefined && 
        (displayName.includes('Kibibytes') || 
         displayName.includes('Mebibytes') || 
         displayName.includes('Gibibytes') || 
         displayName.includes('Tebibytes') ||
         displayName.includes('Pebibytes') ||
         displayName === 'Bytes');
}